import type { RootStore } from "@state/stores/types";
import DepositStore from "./DepositStore";
import { action, computed, observable, runInAction } from "mobx";
import { DepositLimitData } from "@data-types/deposit/DepositLimitData";
import {
	DepositAndLossLimitSubmit,
	DepositLimitFormType,
	LimitCategoryTypes,
	LimitSubmit,
	ResolveDepositAndLossLimit,
	ResolveLimit,
} from "@data-types";
import { localizationService } from "@state/services";
import { MyLimitsService } from "@services/account-settings/MyLimitsService";
import { AccountTypes, SGUID_EMPTY_CONST } from "@utils";
import { buildLimitIncreaseConsents } from "@utils/account-settings/my-limits/buildLimitIncreaseConsents";
import { LoaderStore } from "@state/stores/common";
import { SubmitLimitAction } from "@state/stores/account-settings/my-limits/MyLimitsStore";
import { LimitSteps } from "@utils/limits/limitSteps";
import { LimitType } from "@utils/limits/limitType";

export default class DepositLimitStore {
	rootStore: RootStore;
	depositStore: DepositStore;
	loaderStore: LoaderStore;

	@observable isDepositLimitPopupActive: boolean = false;
	@observable isPartialDepositAmountActive: boolean = false;
	@observable activeStep: LimitSteps = LimitSteps.NONE;
	@observable limitData: DepositLimitData | null = null;
	@observable activeAccount: AccountTypes;
	@observable startLossLimitValue: number = 0;

	constructor(rootStore: RootStore, depositStore: DepositStore) {
		this.rootStore = rootStore;
		this.depositStore = depositStore;
		this.loaderStore = new LoaderStore();

		this.activeAccount = rootStore.userAuthStore.isInternetUser
			? AccountTypes.ONLINE
			: AccountTypes.OFFLINE;
	}

	@computed
	get isLoading() {
		return this.loaderStore.isLoading;
	}

	@computed
	get isLoadingProcess() {
		return this.loaderStore.isLoadingProcess;
	}

	@action.bound
	onSuccess(data: DepositLimitFormType) {
		const limitType =
			this.limitData?.userLimits[0]?.gamingLimitDuration.abrv;

		if (limitType === LimitType.MONTHLY_LIMIT && "lossAmount" in data) {
			this.submitMonthlyLimitForm(data);
		} else {
			if (
				limitType === LimitType.DAILY_LIMIT ||
				limitType === LimitType.WEEKLY_LIMIT
			) {
				this.submitDailyOrWeeklyForm(data);
			}
		}
	}

	@action.bound
	async submitDailyOrWeeklyForm(formData: LimitSubmit) {
		const model: ResolveLimit = {
			amount: formData.amount,
			action: SubmitLimitAction.UPDATE,
			categoryAbrv:
				this.limitData?.userLimits[0]?.gamingLimitCategory.abrv || "",
			durationAbrv:
				this.limitData?.userLimits[0]?.gamingLimitDuration.abrv || "",
			gamingLimitTypeAbrv:
				this.limitData?.userLimits[0]?.gamingLimitType.abrv || "",
			limitId: this.limitData?.userLimits[0]?.id || "",
		};

		await this.submitDailyOrWeeklyLimit(model);
	}

	@action.bound
	async submitMonthlyLimitForm(formData: DepositAndLossLimitSubmit) {
		if (this.limitData?.availableBudget != null) {
			this.rootStore.accountVerificationStore.setPartialDepositAmount(
				this.limitData?.availableBudget
			);

			this.rootStore.accountVerificationStore.setPartialDepositFunction(
				() => this.deactivateDepositLimitPopup(true)
			);
		}

		const isDepositLimitChanged =
			Number(formData.amount) ==
			Number(this.limitData?.userLimits[0]?.amount)
				? SubmitLimitAction.NONE
				: SubmitLimitAction.UPDATE;

		const isLossLimitChanged =
			Number(formData.lossAmount) ==
			Number(this.limitData?.userLimits[1]?.amount)
				? SubmitLimitAction.NONE
				: SubmitLimitAction.UPDATE;

		const model: ResolveDepositAndLossLimit = {
			amount: Number(formData.amount),
			gamingLimitAction: isDepositLimitChanged,
			gamingLimitCategoryAbrv:
				this.limitData?.userLimits[0]?.gamingLimitCategory.abrv || "",
			gamingLimitDurationAbrv:
				this.limitData?.userLimits[0]?.gamingLimitDuration.abrv || "",
			gamingLimitTypeAbrv:
				this.limitData?.userLimits[0]?.gamingLimitType.abrv || "",
			limitId:
				this.limitData?.userLimits[0].id === SGUID_EMPTY_CONST
					? null
					: this.limitData?.userLimits[0]?.id || "",

			lossAmount: Number(formData.lossAmount),
			lossGamingLimitAction: isLossLimitChanged,
			lossGamingLimitCategoryAbrv:
				this.limitData?.userLimits[1]?.gamingLimitCategory.abrv ||
				LimitCategoryTypes.SPORTS_BETTING,
			lossGamingLimitDurationAbrv:
				this.limitData?.userLimits[1]?.gamingLimitDuration.abrv ||
				LimitType.MONTHLY_LIMIT,
			lossGamingLimitTypeAbrv:
				this.limitData?.userLimits[1]?.gamingLimitType.abrv ||
				"loss-limit-de",
			lossLimitId:
				this.limitData?.userLimits[1]?.id === SGUID_EMPTY_CONST
					? null
					: this.limitData?.userLimits[1]?.id || null,

			activeGamingLimitCategoryAbrv:
				this.limitData?.userLimits[1]?.gamingLimitCategory.abrv ||
				this.limitData?.userLimits[0]?.gamingLimitCategory.abrv ||
				"",
			consents: buildLimitIncreaseConsents(formData, (key: string) =>
				localizationService.t(key)
			),
		};

		await this.submitMonthlyDepositLimitWithLoss(model);
	}

	@action.bound
	fetchStarterLossLimitValue() {
		if (this.limitData && this.limitData.userLimits?.[1]) {
			this.startLossLimitValue = this.limitData.userLimits[1].amount || 0;
		} else {
			this.startLossLimitValue = 0;
		}
	}

	@action.bound
	getLimitName() {
		const gamingLimitDuration =
			this.limitData?.userLimits?.[0]?.gamingLimitDuration;

		if (!gamingLimitDuration?.name) {
			return "";
		}

		return gamingLimitDuration.name.split(" ")[0].toLowerCase();
	}

	@action.bound
	setStarterLossLimitValue(startLossLimitValue: number) {
		this.startLossLimitValue = startLossLimitValue;
	}

	@action.bound
	async submitMonthlyDepositLimitWithLoss(model: ResolveDepositAndLossLimit) {
		try {
			this.loaderStore.suspend();

			const response = await MyLimitsService.resolveDepositLimitWithLoss(
				this.activeAccount,
				model
			);

			if (response) {
				runInAction(() => {
					this.rootStore.notificationStore.success(
						response?.infoMessage ||
							"error-handling:GENERIC_ERROR"
					);

					if (
						this.limitData?.userLimits?.[0].amount &&
						this.limitData?.userLimits?.[0].amount < model.amount &&
						model.amount > 1000
					) {
						this.activeStep = LimitSteps.CREDIT_RATING;
					} else if (
						this.limitData?.availableBudget &&
						this.limitData?.userLimits?.[0].amount < model.amount
					) {
						this.activeStep =
							LimitSteps.PARTIAL_DEPOSIT_AFTER_LIMIT_INCREASE;
					} else {
						this.deactivateAllSteps();
						this.deactivateDepositLimitPopup();
					}
				});
			}
		} catch (error) {
			console.error(error);
			if (error?.errorCode === 20) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/account-verification",
					"ACCOUNT_VERIFICATION.TITLE",
					localizationService.t(
						"ACCOUNT_VERIFICATION.ACC_VERIFICATION_NAVIGATION"
					),
					false
				);
			} else if (error?.errorCode === 21) {
				this.rootStore.accountVerificationStore.showPopup(
					"limit-increase",
					"LIMIT_INCREASE.TITLE",
					localizationService.t(
						"LIMIT_INCREASE.LIMIT_INCREASE_NAVIGATION"
					),
					false
				);

				this.deactivateDepositLimitPopup(false);
			} else if (error?.errorCode === 400242) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400244) {
				const errorMessage = localizationService.t(
					"COMMON.LIMITS.LUGAS_LIMIT_EXCEEDS_MAX"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400245) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400256) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 35) {
				this.rootStore.notificationStore.error(error?.message);
			} else {
				const errorMessage = localizationService.t(
					error?.message || "COMMON.LIMITS." + error
				);
				this.rootStore.notificationStore.error(errorMessage);
			}
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async submitDailyOrWeeklyLimit(model: ResolveLimit) {
		this.loaderStore.suspend();
		try {
			const response = await MyLimitsService.resolveLimit(
				this.activeAccount,
				model
			);
			if (response) {
				runInAction(() => {
					this.rootStore.notificationStore.success(
						response?.infoMessage ||
							"error-handling:GENERIC_ERROR"
					);
				});

				if (
					this.limitData?.availableBudget &&
					this.limitData?.userLimits?.[0].amount < model.amount
				) {
					this.activeStep =
						LimitSteps.PARTIAL_DEPOSIT_AFTER_LIMIT_INCREASE;
				} else {
					this.deactivateAllSteps();
					this.deactivateDepositLimitPopup();
				}
			}
		} catch (error) {
			console.error(error);
			if (error?.errorCode === 20) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/account-verification",
					"ACCOUNT_VERIFICATION.TITLE",
					localizationService.t(
						"ACCOUNT_VERIFICATION.ACC_VERIFICATION_NAVIGATION"
					),
					false
				);
			} else if (error?.errorCode === 21) {
				this.rootStore.accountVerificationStore.showPopup(
					"limit-increase",
					"LIMIT_INCREASE.TITLE",
					localizationService.t(
						"LIMIT_INCREASE.LIMIT_INCREASE_NAVIGATION"
					),
					false
				);

				this.deactivateDepositLimitPopup(false);
			} else if (error?.errorCode === 400242) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400244) {
				const errorMessage = localizationService.t(
					"COMMON.LIMITS.LUGAS_LIMIT_EXCEEDS_MAX"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400245) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400256) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else {
				const errorMessage = localizationService.t(
					error?.message || "COMMON.LIMITS." + error
				);
				this.rootStore.notificationStore.error(errorMessage);
			}
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	activateDepositLimitPopup(limitData: DepositLimitData) {
		this.isDepositLimitPopupActive = true;

		this.limitData = limitData;
	}

	@action.bound
	deactivateDepositLimitPopup(isPartialDepositSelected?: boolean) {
		this.isDepositLimitPopupActive = false;
		this.activeStep = LimitSteps.NONE;

		if (isPartialDepositSelected && this.limitData?.availableBudget) {
			this.depositStore.setPartialDepositAmount(
				this.limitData?.availableBudget
			);

			this.depositStore.resetMultiStepFlags();
			this.isPartialDepositAmountActive = true;
		}
	}

	@action.bound
	activateIncreaseLimitForm() {
		this.activeStep = LimitSteps.INCREASE_LIMIT;
	}

	@action.bound
	activateCreditRatingCheck() {
		this.activeStep = LimitSteps.CREDIT_RATING;
	}

	@action.bound
	deactivateAllSteps() {
		this.activeStep = LimitSteps.NONE;
	}

	@action.bound
	onDispose() {
		this.isPartialDepositAmountActive = false;
		this.isDepositLimitPopupActive = false;
		this.activeStep = LimitSteps.NONE;
		this.limitData = null;
	}
}
